import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate, useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  isLightColor,
  Slider,
  softBadgeColors,
  Spinner,
  StarRating,
  useInfiniteScroll,
} from '@structural-climbing/ui'
import {
  type Discipline,
  disciplineForSection,
  gradeDisplay,
  gradesForDiscipline,
} from '@structural-climbing/validation'
import { type GymRoute } from '../features/routes/useGymRoutes'
import {
  type RouteFilterOptions,
  useRouteFilterOptions,
} from '../features/routes/useRouteFilterOptions'
import { useMemberCheckInStatus } from '../features/checkin/useMemberCheckInStatus'
import { useGymBranding } from '../features/gym/useGymBranding'
import { useGymGradeConfig } from '../features/gym/useGymGradeConfig'
import { GymMapModal } from '../features/gym/GymMapModal'
import { RouteDetailSheet } from '../features/routes/RouteDetailSheet'
import { WallRankCard } from '../features/standings/WallRankCard'
import { StandingsSheet } from '../features/standings/StandingsSheet'
import { disciplinePath } from '../features/nav/navGroups'
import { Sheet } from '../design/Sheet'
import { Page } from '../design/Page'
import { EmptyState } from '../design/EmptyState'
import { fadeRise, staggerIndex } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'

const PAGE_SIZE = 20

function isNewRoute(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 3 * 24 * 60 * 60 * 1000
}

interface Section {
  id: string
  name: string
  discipline: string | null
}

interface FilterState {
  filterClimbed: 'all' | 'climbed' | 'unclimbed'
  filterGrades: number[]
  filterAge: 'all' | 'new' | 'old'
  sortBy: 'date' | 'ascents'
  filterSection: string | null
  // Lead tab only: who set it, the hold colour, and an inclusive grade range.
  filterSetter: string | null
  filterColor: string | null
  filterGradeRange: [number, number] | null
}

const DEFAULT_FILTERS: FilterState = {
  filterClimbed: 'all',
  filterGrades: [],
  filterAge: 'all',
  sortBy: 'date',
  filterSection: null,
  filterSetter: null,
  filterColor: null,
  filterGradeRange: null,
}

// Stored per discipline: grade ranks, sectors, setters and colours only mean
// something on their own wall, so each page remembers its own filters.
function filtersKey(gymId: string | null, discipline: Discipline) {
  return gymId ? `routes-filters-${gymId}-${discipline}` : null
}

function loadFilters(gymId: string | null, discipline: Discipline): FilterState {
  const key = filtersKey(gymId, discipline)
  if (!key) return DEFAULT_FILTERS
  try {
    const stored = sessionStorage.getItem(key)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<FilterState> & { filterGrade?: number | null }
      const merged = { ...DEFAULT_FILTERS, ...parsed }
      // Migrate the legacy single-grade filter to the multi-grade array
      if (!Array.isArray(merged.filterGrades)) {
        merged.filterGrades = typeof parsed.filterGrade === 'number' ? [parsed.filterGrade] : []
      }
      return merged
    }
  } catch {
    // corrupted storage — fall back to defaults
  }
  return DEFAULT_FILTERS
}

function activeFilterCount(f: FilterState): number {
  let n = 0
  if (f.filterClimbed !== 'all') n++
  if (f.filterGrades.length > 0) n++
  if (f.filterAge !== 'all') n++
  if (f.sortBy !== 'date') n++
  if (f.filterSection !== null) n++
  if (f.filterSetter !== null) n++
  if (f.filterColor !== null) n++
  if (f.filterGradeRange !== null) n++
  return n
}

function FilterSheet({
  open,
  initialFilters,
  sections,
  discipline,
  filterOptions,
  onApply,
  onClose,
}: {
  open: boolean
  initialFilters: FilterState
  sections: Section[]
  discipline: Discipline
  filterOptions: RouteFilterOptions
  onApply: (filters: FilterState) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const [pending, setPending] = useState<FilterState>(initialFilters)

  // Filter by rank (what the backend `?grades=` expects), labelled from the gym's
  // scale for the active discipline tab.
  const scale = gradesForDiscipline(gradeConfig, discipline)
  const gradeOptions = scale.map((g) => ({ rank: g.rank, label: g.label }))
  const gradeLabel = (rank: number) => scale.find((g) => g.rank === rank)?.label ?? String(rank)

  // Lead walls grade on the 27-step French scale, so a grade is picked as a
  // range between the easiest and hardest route actually on the wall.
  const isLead = discipline === 'lead'
  const rangeMin = filterOptions.grade_min
  const rangeMax = filterOptions.grade_max
  const showGradeRange = isLead && rangeMin !== null && rangeMax !== null && rangeMax > rangeMin
  const gradeRange: [number, number] =
    pending.filterGradeRange && rangeMin !== null && rangeMax !== null
      ? [
          Math.max(pending.filterGradeRange[0], rangeMin),
          Math.min(pending.filterGradeRange[1], rangeMax),
        ]
      : [rangeMin ?? 1, rangeMax ?? 1]

  // A selected chip is filled with the gym's own colour; the contrast token
  // keeps the label readable whether that colour is near-black or near-white.
  function chipClass(active: boolean) {
    return active
      ? 'bg-[var(--gym-primary)] font-bold text-[var(--gym-on-primary)]'
      : 'text-foreground/70'
  }

  return (
    <Sheet isOpen={open} onClose={onClose} aria-label={t('routes.filtersTitle')}>
      <div data-testid="filter-sheet" className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between border-b border-foreground/10 px-5 pb-2 pt-2">
          <h2 className="text-base font-semibold">{t('routes.filtersTitle')}</h2>
          <button
            type="button"
            className="rounded-full p-1.5 text-foreground/40 hover:bg-content2 hover:text-foreground active:bg-content3"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-3">
          {sections.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
                {t('routes.filterSectionLabel')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="light"
                  size="sm"
                  className={chipClass(pending.filterSection === null)}
                  onClick={() => setPending((p) => ({ ...p, filterSection: null }))}
                >
                  {t('routes.filterAllSections')}
                </Button>
                {sections.map((s) => {
                  const active = pending.filterSection === s.id
                  return (
                    <Button
                      key={s.id}
                      variant="light"
                      size="sm"
                      className={chipClass(active)}
                      onClick={() => setPending((p) => ({ ...p, filterSection: s.id }))}
                    >
                      {s.name}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
              {t('routes.filterClimbedLabel')}
            </p>
            <div className="flex flex-wrap gap-2">
              {(['all', 'climbed', 'unclimbed'] as const).map((v) => {
                const label =
                  v === 'all'
                    ? t('routes.filterAll')
                    : v === 'climbed'
                      ? t('routes.filterClimbed')
                      : t('routes.filterUnclimbed')
                const active = pending.filterClimbed === v
                return (
                  <Button
                    key={v}
                    variant="light"
                    size="sm"
                    className={chipClass(active)}
                    onClick={() => setPending((p) => ({ ...p, filterClimbed: v }))}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
          </div>

          {isLead ? (
            showGradeRange && (
              <div data-testid="grade-range-filter">
                <p className="mb-2 flex justify-between text-xs font-medium uppercase tracking-wide text-foreground/50">
                  <span>{t('routes.filterGradeLabel')}</span>
                  <span className="normal-case text-foreground">
                    {gradeLabel(gradeRange[0])} – {gradeLabel(gradeRange[1])}
                  </span>
                </p>
                <Slider
                  aria-label={t('routes.filterGradeLabel')}
                  size="sm"
                  step={1}
                  minValue={rangeMin}
                  maxValue={rangeMax}
                  value={gradeRange}
                  getValue={(v) => {
                    const [lo, hi] = v as number[]
                    return `${gradeLabel(lo)} – ${gradeLabel(hi)}`
                  }}
                  onChange={(v) => {
                    const [lo, hi] = v as number[]
                    // The full span is the same as no filter; store it as such so
                    // new harder routes aren't silently excluded later.
                    const whole = lo <= rangeMin && hi >= rangeMax
                    setPending((p) => ({ ...p, filterGradeRange: whole ? null : [lo, hi] }))
                  }}
                  classNames={{
                    filler: 'bg-[var(--gym-primary)]',
                    thumb: 'bg-[var(--gym-primary)]',
                  }}
                />
              </div>
            )
          ) : (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
                {t('routes.filterGradeLabel')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="light"
                  size="sm"
                  className={chipClass(pending.filterGrades.length === 0)}
                  onClick={() => setPending((p) => ({ ...p, filterGrades: [] }))}
                >
                  {t('routes.filterAll')}
                </Button>
                {gradeOptions.map(({ rank, label }) => {
                  const active = pending.filterGrades.includes(rank)
                  return (
                    <Button
                      key={rank}
                      variant="light"
                      size="sm"
                      className={chipClass(active)}
                      onClick={() =>
                        setPending((p) => ({
                          ...p,
                          filterGrades: active
                            ? p.filterGrades.filter((x) => x !== rank)
                            : [...p.filterGrades, rank],
                        }))
                      }
                    >
                      {label}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          {isLead && filterOptions.colors.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
                {t('routes.filterColorLabel')}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="light"
                  size="sm"
                  className={chipClass(pending.filterColor === null)}
                  onClick={() => setPending((p) => ({ ...p, filterColor: null }))}
                >
                  {t('routes.filterAll')}
                </Button>
                {filterOptions.colors.map((c) => {
                  const active = pending.filterColor === c
                  return (
                    <Button
                      key={c}
                      isIconOnly
                      variant="light"
                      size="sm"
                      aria-label={c}
                      aria-pressed={active}
                      className={
                        active
                          ? 'ring-2 ring-[var(--gym-primary)] ring-offset-2 ring-offset-content1'
                          : ''
                      }
                      onClick={() => setPending((p) => ({ ...p, filterColor: active ? null : c }))}
                    >
                      <span
                        className={`h-6 w-6 rounded-full ${isLightColor(c) ? 'border border-foreground/30' : 'border border-foreground/10'}`}
                        style={{ backgroundColor: c }}
                      />
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          {isLead && filterOptions.setters.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
                {t('routes.filterSetterLabel')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="light"
                  size="sm"
                  className={chipClass(pending.filterSetter === null)}
                  onClick={() => setPending((p) => ({ ...p, filterSetter: null }))}
                >
                  {t('routes.filterAllSetters')}
                </Button>
                {filterOptions.setters.map((s) => {
                  const active = pending.filterSetter === s.id
                  return (
                    <Button
                      key={s.id}
                      variant="light"
                      size="sm"
                      className={chipClass(active)}
                      onClick={() => setPending((p) => ({ ...p, filterSetter: s.id }))}
                    >
                      {s.name}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
              {t('routes.filterAgeLabel')}
            </p>
            <div className="flex flex-wrap gap-2">
              {(['all', 'new', 'old'] as const).map((v) => {
                const label =
                  v === 'all'
                    ? t('routes.filterAll')
                    : v === 'new'
                      ? t('routes.filterNew')
                      : t('routes.filterOld')
                const active = pending.filterAge === v
                return (
                  <Button
                    key={v}
                    variant="light"
                    size="sm"
                    className={chipClass(active)}
                    onClick={() => setPending((p) => ({ ...p, filterAge: v }))}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground/50">
              {t('routes.sortLabel')}
            </p>
            <div className="flex flex-wrap gap-2">
              {(['date', 'ascents'] as const).map((v) => {
                const label = v === 'date' ? t('routes.sortByDate') : t('routes.sortByAscents')
                const active = pending.sortBy === v
                return (
                  <Button
                    key={v}
                    variant="light"
                    size="sm"
                    className={chipClass(active)}
                    onClick={() => setPending((p) => ({ ...p, sortBy: v }))}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-3 border-t border-foreground/10 px-5 pb-2 pt-3">
          <Button
            variant="light"
            size="md"
            className="flex-1"
            onClick={() => onApply(DEFAULT_FILTERS)}
          >
            {t('routes.resetFilters')}
          </Button>
          <Button
            variant="solid"
            size="md"
            className="flex-1 bg-[var(--gym-primary)] font-semibold text-[var(--gym-on-primary)]"
            onClick={() => onApply(pending)}
          >
            {t('routes.applyFilters')}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}

function RouteRow({
  route,
  apiUrl,
  locale,
  discipline,
  onClick,
}: {
  route: GymRoute
  apiUrl: string
  locale: string
  discipline: Discipline
  onClick: () => void
}) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const grade = gradeDisplay(gradeConfig, route.grade, discipline)
  const badge = softBadgeColors(grade.color)
  const isNew = isNewRoute(route.created_at)
  const dateLabel = new Date(route.created_at).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  })

  return (
    <div
      className="flex cursor-pointer items-center gap-3 rounded-xl border border-divider bg-content1 p-3 active:opacity-70"
      onClick={onClick}
    >
      {route.image_r2_key ? (
        <img
          src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
          alt={t('routes.gradeBadge', { grade: grade.label })}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-content2 text-xl text-foreground/30">
          ⛰
        </div>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          className="h-4 w-4 shrink-0 rounded-full border border-foreground/10"
          style={{ backgroundColor: route.color ?? '#9ca3af' }}
        />
        <span
          data-testid="grade-badge"
          className="rounded px-2 py-0.5 text-sm font-semibold"
          style={{ backgroundColor: badge.bg, color: badge.fg }}
        >
          {grade.label}
        </span>
        {isNew && (
          <span className="rounded bg-success px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
            {t('routes.new')}
          </span>
        )}
        {route.has_ascent && (
          <span
            data-testid="climbed-badge"
            className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-primary"
          >
            {t('routes.climbedBadge')}
          </span>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <p className="text-xs text-foreground/40">{t('routes.dateSet', { date: dateLabel })}</p>
        <p className="text-xs text-foreground/30">
          {route.ascent_count === 0
            ? t('routes.topNone')
            : t('routes.topCount', { count: route.ascent_count })}
        </p>
        {route.avg_rating != null && (
          <StarRating
            value={route.avg_rating}
            showValue
            size="sm"
            className="text-xs text-foreground/40"
            label={t('routes.ratingOutOf', { value: route.avg_rating.toFixed(1) })}
          />
        )}
      </div>
    </div>
  )
}

function groupBySection(routes: GymRoute[], sections: Section[], noSectionLabel: string) {
  const groups: { id: string | null; name: string; routes: GymRoute[] }[] = []

  for (const section of sections) {
    const sectionRoutes = routes.filter((r) => r.section?.id === section.id)
    if (sectionRoutes.length > 0)
      groups.push({ id: section.id, name: section.name, routes: sectionRoutes })
  }

  const unsectioned = routes.filter((r) => r.section === null)
  if (unsectioned.length > 0) groups.push({ id: null, name: noSectionLabel, routes: unsectioned })

  groups.sort((a, b) => {
    const newest = (g: (typeof groups)[number]) =>
      Math.max(...g.routes.map((r) => new Date(r.created_at).getTime()))
    return newest(b) - newest(a)
  })

  return groups as { id: string | null; name: string; routes: GymRoute[] }[]
}

interface Props {
  /** Bouldering and lead each have their own page, picked by the URL. */
  discipline: Discipline
  /**
   * True on `/:gymSlug/<discipline>/standings`. The leaderboard is a sheet over
   * the wall rather than a page of its own, but it keeps its URL so links to it
   * (the dashboard teaser, the desktop sidebar, a bookmark) still land on it.
   */
  standingsOpen?: boolean
}

export function RoutesPage({ discipline, standingsOpen = false }: Props) {
  const { gymId, gymSlug, hasActiveSubscription } = useOutletContext<GymContext>()
  const navigate = useNavigate()
  const location = useLocation()
  const { getAccessTokenSilently, user } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [selected, setSelected] = useState<GymRoute | null>(null)
  const [filters, setFilters] = useState<FilterState>(() => loadFilters(gymId, discipline))
  const [sheetOpen, setSheetOpen] = useState(false)
  const [mapModalOpen, setMapModalOpen] = useState(false)
  const [routes, setRoutes] = useState<GymRoute[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [sections, setSections] = useState<Section[]>([])

  const gymBranding = useGymBranding(gymId, apiUrl)
  const filterOptions = useRouteFilterOptions(
    gymId,
    apiUrl,
    getAccessTokenSilently,
    discipline,
    discipline === 'lead',
  )

  // A member who used their last entry to check in has a `depleted` subscription but is
  // physically in the gym — they must still be able to log routes. `hasActiveSubscription`
  // is fetched once when the layout mounts, so it misses a check-in that happens afterwards.
  // Track the live check-in state (realtime) and OR it into the gate, mirroring DashboardPage.
  const { isCheckedIn } = useMemberCheckInStatus(
    user?.sub as string | undefined,
    gymId,
    apiUrl,
    getAccessTokenSilently,
  )
  const canLogAscent = hasActiveSubscription || isCheckedIn

  // Opening pushes the standings URL, so the phone's back gesture closes the
  // sheet. Closing pops that entry when we pushed it, and otherwise (a link
  // straight to the standings) replaces it, so back never reopens the sheet.
  const wallPath = `/${gymSlug}/${disciplinePath(discipline)}`
  const openStandings = useCallback(() => {
    navigate(`${wallPath}/standings`, { state: { fromWall: true } })
  }, [navigate, wallPath])
  const closeStandings = useCallback(() => {
    if ((location.state as { fromWall?: boolean } | null)?.fromWall) navigate(-1)
    else navigate(`${wallPath}/routes`, { replace: true })
  }, [location.state, navigate, wallPath])

  const standingsSheet = gymId ? (
    <StandingsSheet
      isOpen={standingsOpen}
      onClose={closeStandings}
      gymId={gymId}
      gymSlug={gymSlug}
      discipline={discipline}
    />
  ) : null

  const currentOffsetRef = useRef(0)
  const currentFiltersRef = useRef(filters)
  const pendingNavRef = useRef<string | null>(null)

  // Load sections once on mount
  useEffect(() => {
    if (!gymId) return
    async function loadSections() {
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({ gym_id: gymId! })
        const res = await fetch(`${apiUrl}/my-gym-sections?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) setSections((await res.json()) as Section[])
      } catch {
        // sections are an enhancement — silently ignore
      }
    }
    void loadSections()
  }, [gymId, apiUrl, getAccessTokenSilently])

  const loadPage = useCallback(
    async (currentFilters: FilterState, pageOffset: number, append: boolean) => {
      if (!append) setLoading(true)
      else setLoadingMore(true)
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({
          status: 'active',
          limit: String(PAGE_SIZE),
          offset: String(pageOffset),
        })
        if (gymId) params.set('gym_id', gymId)
        params.set('discipline', discipline)
        if (currentFilters.filterGrades.length > 0)
          params.set('grades', currentFilters.filterGrades.join(','))
        if (currentFilters.filterClimbed !== 'all')
          params.set('filter_climbed', currentFilters.filterClimbed)
        if (currentFilters.filterAge !== 'all') params.set('filter_age', currentFilters.filterAge)
        if (currentFilters.sortBy !== 'date') params.set('sort_by', currentFilters.sortBy)
        if (currentFilters.filterSection !== null)
          params.set('section_id', currentFilters.filterSection)
        if (currentFilters.filterSetter !== null)
          params.set('setter_id', currentFilters.filterSetter)
        if (currentFilters.filterColor !== null) params.set('color', currentFilters.filterColor)
        if (currentFilters.filterGradeRange !== null) {
          params.set('grade_min', String(currentFilters.filterGradeRange[0]))
          params.set('grade_max', String(currentFilters.filterGradeRange[1]))
        }

        const res = await fetch(`${apiUrl}/my-gym-routes?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const body = (await res.json()) as {
            routes: GymRoute[]
            has_more: boolean
            total: number
          }
          if (append) {
            setRoutes((prev) => [...prev, ...body.routes])
          } else {
            setRoutes(body.routes)
          }
          setHasMore(body.has_more)
          currentOffsetRef.current = pageOffset
        }
      } finally {
        if (!append) setLoading(false)
        else setLoadingMore(false)
      }
    },
    [getAccessTokenSilently, apiUrl, gymId, discipline],
  )

  useEffect(() => {
    currentFiltersRef.current = filters
    pendingNavRef.current = null
    setSelected(null)
    setRoutes([])
    currentOffsetRef.current = 0
    void loadPage(filters, 0, false)
  }, [filters, loadPage])

  useEffect(() => {
    const key = filtersKey(gymId, discipline)
    if (!key) return
    try {
      sessionStorage.setItem(key, JSON.stringify(filters))
    } catch {
      // Storage blocked: filters still work, they just don't survive a reload.
    }
  }, [gymId, discipline, filters])

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return
    const nextOffset = currentOffsetRef.current + PAGE_SIZE
    void loadPage(currentFiltersRef.current, nextOffset, true)
  }, [hasMore, loadingMore, loadPage])

  const sentinelRef = useInfiniteScroll(loadMore, hasMore)

  const selectedIndex = selected ? routes.findIndex((r) => r.id === selected.id) : -1
  const hasPrev = selectedIndex > 0
  const hasNext = selectedIndex !== -1 && (selectedIndex < routes.length - 1 || hasMore)

  const handlePrev = useCallback(() => {
    if (selectedIndex > 0) setSelected(routes[selectedIndex - 1])
  }, [routes, selectedIndex])

  const handleNext = useCallback(() => {
    if (selectedIndex === -1 || !selected) return
    if (selectedIndex < routes.length - 1) {
      setSelected(routes[selectedIndex + 1])
      return
    }
    if (hasMore && !loadingMore) {
      pendingNavRef.current = selected.id
      loadMore()
    }
  }, [routes, selectedIndex, selected, hasMore, loadingMore, loadMore])

  useEffect(() => {
    const anchorId = pendingNavRef.current
    if (!anchorId || loadingMore) return
    pendingNavRef.current = null
    if (!selected || selected.id !== anchorId) return
    const idx = routes.findIndex((r) => r.id === anchorId)
    if (idx !== -1 && idx < routes.length - 1) setSelected(routes[idx + 1])
  }, [routes, loadingMore, selected])

  // Only the member's own rating is patched optimistically. avg_rating is left to
  // the next fetch on purpose: recomputing a gym-wide average from the one vote
  // this client can see would drift away from the server's number.
  function handleLogAscent(routeId: string, attempts: number | null, rating: number | null) {
    const apply = <T extends GymRoute>(r: T): T => ({
      ...r,
      has_ascent: true,
      attempts: r.has_ascent ? r.attempts : attempts,
      ascent_count: r.has_ascent ? r.ascent_count : r.ascent_count + 1,
      my_rating: rating ?? r.my_rating,
    })
    setRoutes((prev) => prev.map((r) => (r.id === routeId ? apply(r) : r)))
    setSelected((prev) => (prev?.id === routeId ? apply(prev) : prev))
  }

  function handleRemoveAscent(routeId: string) {
    // Deleting the ascent deletes the row the rating lived on, so the member's
    // own rating goes with it.
    const apply = <T extends GymRoute>(r: T): T => ({
      ...r,
      has_ascent: false,
      ascent_count: Math.max(0, r.ascent_count - 1),
      my_rating: null,
    })
    setRoutes((prev) => prev.map((r) => (r.id === routeId ? apply(r) : r)))
    setSelected((prev) => (prev?.id === routeId ? apply(prev) : prev))
  }

  // The average is the server's figure, never recomputed here: the client only
  // knows its own vote and an already-rounded mean, so estimating the new average
  // locally would drift from what the next fetch returns.
  function handleRatingRefresh(routeId: string, avgRating: number | null, ratingCount: number) {
    const apply = <T extends GymRoute>(r: T): T => ({
      ...r,
      avg_rating: avgRating,
      rating_count: ratingCount,
    })
    setRoutes((prev) => prev.map((r) => (r.id === routeId ? apply(r) : r)))
    setSelected((prev) => (prev?.id === routeId ? apply(prev) : prev))
  }

  function handleUpdateAscent(routeId: string, attempts: number | null, rating: number | null) {
    setRoutes((prev) =>
      prev.map((r) => (r.id === routeId ? { ...r, attempts, my_rating: rating } : r)),
    )
    setSelected((prev) => (prev?.id === routeId ? { ...prev, attempts, my_rating: rating } : prev))
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" color="primary" aria-label="Loading" />
        {standingsSheet}
      </div>
    )
  }

  const filterCount = activeFilterCount(filters)

  // Sections belonging to the active discipline tab (rope => lead, else boulder).
  const disciplineSections = sections.filter(
    (s) => disciplineForSection(s.discipline) === discipline,
  )

  // Group by section when sections exist and no section filter is active
  const showGrouped = disciplineSections.length > 0 && filters.filterSection === null
  const groups = showGrouped
    ? groupBySection(routes, disciplineSections, t('routes.noSection'))
    : null

  return (
    <Page width="wide">
      {gymBranding?.map_url && gymBranding.map_file_type && (
        <GymMapModal
          mapUrl={gymBranding.map_url}
          mapFileType={gymBranding.map_file_type}
          isOpen={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
        />
      )}
      {gymId && (
        <motion.div variants={fadeRise}>
          <WallRankCard gymId={gymId} discipline={discipline} onOpen={openStandings} />
        </motion.div>
      )}

      {/* One row, tools pinned right. The discipline is picked by the menu
          pill above, so this row is only the map and the filters. */}
      <motion.div variants={fadeRise} className="flex items-center gap-2">
        <h1 className="sr-only">
          {t('routes.pageTitle')}:{' '}
          {discipline === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering')}
        </h1>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          {gymBranding?.map_url && gymBranding.map_file_type && (
            <button
              type="button"
              onClick={() => setMapModalOpen(true)}
              aria-label={t('routes.seeGymMap')}
              title={t('routes.seeGymMap')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-foreground/60 hover:bg-[var(--gym-veil)] hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.157 2.176a1.5 1.5 0 0 0-1.147 0l-4.084 1.69A1.5 1.5 0 0 0 2 5.25v10.877a1.5 1.5 0 0 0 2.074 1.386l3.51-1.452 4.26 1.762a1.5 1.5 0 0 0 1.146 0l4.083-1.69A1.5 1.5 0 0 0 18 14.75V3.872a1.5 1.5 0 0 0-2.073-1.386l-3.51 1.452-4.26-1.762ZM7.58 5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 7.58 5Zm5.59 2.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="relative flex h-9 w-9 items-center justify-center rounded-full text-foreground/60 hover:bg-[var(--gym-veil)] hover:text-foreground"
            onClick={() => setSheetOpen(true)}
            aria-label={t('routes.filtersButton')}
            title={t('routes.filtersButton')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.629V2.34a.75.75 0 0 1 .628-.74Z"
                clipRule="evenodd"
              />
            </svg>
            {filterCount > 0 && (
              <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--gym-primary)] text-[10px] font-bold leading-none text-[var(--gym-on-primary)]">
                {filterCount}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {routes.length === 0 ? (
        <EmptyState
          icon={filterCount > 0 ? '🔍' : '🧗'}
          title={filterCount > 0 ? t('routes.noMatchingRoutes') : t('routes.noRoutes')}
        />
      ) : groups ? (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <div key={group.id ?? '__none__'}>
              <p data-testid="section-header" className="eyebrow mb-2 px-1 text-foreground/40">
                {group.name}
              </p>
              <div className="flex flex-col gap-2">
                {group.routes.map((route, i) => (
                  <motion.div key={route.id} {...staggerIndex(i)}>
                    <RouteRow
                      route={route}
                      apiUrl={apiUrl}
                      locale={i18n.language}
                      discipline={discipline}
                      onClick={() => setSelected(route)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
          {loadingMore && (
            <div className="flex justify-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          <div ref={sentinelRef} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {routes.map((route, i) => (
            <motion.div key={route.id} {...staggerIndex(i)}>
              <RouteRow
                route={route}
                apiUrl={apiUrl}
                locale={i18n.language}
                discipline={discipline}
                onClick={() => setSelected(route)}
              />
            </motion.div>
          ))}
          {loadingMore && (
            <div className="flex justify-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          <div ref={sentinelRef} />
        </div>
      )}

      <FilterSheet
        key={String(sheetOpen)}
        open={sheetOpen}
        initialFilters={filters}
        sections={disciplineSections}
        discipline={discipline}
        filterOptions={filterOptions}
        onApply={(newFilters) => {
          setFilters(newFilters)
          setSheetOpen(false)
        }}
        onClose={() => setSheetOpen(false)}
      />

      <RouteDetailSheet
        route={selected}
        gymId={gymId}
        userId={user?.sub ?? ''}
        apiUrl={apiUrl}
        discipline={discipline}
        getAccessTokenSilently={getAccessTokenSilently}
        onClose={() => setSelected(null)}
        onLogAscent={handleLogAscent}
        onRemoveAscent={handleRemoveAscent}
        onUpdateAscent={handleUpdateAscent}
        onRatingRefresh={handleRatingRefresh}
        isArchived={false}
        canLogAscent={canLogAscent}
        hasPrev={hasPrev}
        hasNext={hasNext}
        nextLoading={loadingMore}
        onPrev={handlePrev}
        onNext={handleNext}
      />

      {standingsSheet}
    </Page>
  )
}
